/**
 * Security Configuration
 * Central configuration for all security-related settings
 */

export const SECURITY_CONFIG = {
  // Session Management
  SESSION: {
    TIMEOUT: parseInt(process.env.REACT_APP_SESSION_TIMEOUT) || 900000, // 15 minutes
    WARNING_TIME: parseInt(process.env.REACT_APP_SESSION_WARNING_TIME) || 60000, // 1 minute
    USE_SESSION_STORAGE: true, // Use sessionStorage instead of localStorage
  },

  // Password Policy
  PASSWORD: {
    MIN_LENGTH: 8,
    MAX_LENGTH: 16,
    REQUIRE_UPPERCASE: true,
    REQUIRE_LOWERCASE: true,
    REQUIRE_NUMBER: true,
    REQUIRE_SPECIAL: true,
    SPECIAL_CHARS: "!@#$%^&*()_+-=[]{}:'\"|,.<>/?",
  },

  // File Upload Security
  FILE_UPLOAD: {
    MAX_SIZE: 5 * 1024 * 1024, // 5MB
    ALLOWED_IMAGE_TYPES: [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/bmp",
      "image/webp",
    ],
    FORBIDDEN_EXTENSIONS: ["svg", "exe", "bat", "sh", "php", "js"],
  },

  // Input Validation
  INPUT: {
    MAX_LENGTH: 10000,
    SANITIZE_HTML: true,
    STRIP_SCRIPTS: true,
    ALLOWED_TAGS: [], // Empty = strip all HTML tags
  },

  // API Security
  API: {
    ENABLE_CSRF: true,
    CSRF_HEADER: "X-CSRF-Token",
    RATE_LIMIT_RETRY_AFTER: 5000, // 5 seconds
    MAX_RETRIES: 3,
  },

  // reCAPTCHA
  RECAPTCHA: {
    SITE_KEY: process.env.REACT_APP_RECAPTCHA_SITE_KEY || "",
    ENABLED: process.env.NODE_ENV === "production",
    THRESHOLD: 0.5, // v3 score threshold
  },

  // Encryption
  ENCRYPTION: {
    ALGORITHM: "AES",
    KEY: process.env.REACT_APP_ENCRYPTION_KEY || "default-key",
    STORAGE_KEY: process.env.REACT_APP_STORAGE_ENCRYPTION_KEY || "default-storage-key",
  },

  // Content Security
  CSP: {
    ENABLE_STRICT_MODE: process.env.NODE_ENV === "production",
    REPORT_VIOLATIONS: false,
  },

  // Error Handling
  ERRORS: {
    SHOW_STACK_TRACE: process.env.NODE_ENV === "development",
    LOG_TO_CONSOLE: process.env.NODE_ENV === "development",
    SANITIZE_ERROR_MESSAGES: process.env.NODE_ENV === "production",
  },

  // Production Security Features
  PRODUCTION: {
    DISABLE_CONSOLE: true,
    DISABLE_DEVTOOLS: true,
    ENABLE_STRICT_HEADERS: true,
    ENABLE_SUBRESOURCE_INTEGRITY: true,
  },
};

/**
 * Get security config value by path
 * @param {string} path - Dot-notation path (e.g., "SESSION.TIMEOUT")
 * @returns {any} Config value
 */
export const getSecurityConfig = (path) => {
  return path.split(".").reduce((obj, key) => obj?.[key], SECURITY_CONFIG);
};

/**
 * Validate if current environment is production
 * @returns {boolean}
 */
export const isProduction = () => {
  return process.env.NODE_ENV === "production";
};

/**
 * Validate if current environment is development
 * @returns {boolean}
 */
export const isDevelopment = () => {
  return process.env.NODE_ENV === "development";
};

/**
 * Get sanitized error message for display
 * @param {Error} error - Error object
 * @returns {string} Sanitized error message
 */
export const getSanitizedErrorMessage = (error) => {
  if (isDevelopment()) {
    return error.message || "An error occurred";
  }

  // In production, return generic messages
  const genericMessages = {
    network: "Network error. Please check your connection.",
    auth: "Authentication failed. Please login again.",
    validation: "Invalid input. Please check your data.",
    server: "Server error. Please try again later.",
    default: "An unexpected error occurred. Please try again.",
  };

  // Map error types to generic messages
  if (error.message?.includes("network") || error.message?.includes("fetch")) {
    return genericMessages.network;
  }
  if (error.message?.includes("auth") || error.message?.includes("token")) {
    return genericMessages.auth;
  }
  if (error.message?.includes("validation") || error.message?.includes("invalid")) {
    return genericMessages.validation;
  }

  return genericMessages.default;
};

export default SECURITY_CONFIG;
